import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type FlyoutPanelMode = 'closed' | 'minimized' | 'floating' | 'docked' | 'fullscreen';

interface InputInjection {
  id: number;
  text: string;
}

/** Dateien (+ Text) in die Chat-Eingabe legen — z. B. „Tickets aus Datei" im KIAN-Fenster. */
interface FileInjection {
  id: number;
  files: File[];
  text?: string;
}

interface KianFlyoutState {
  panelMode: FlyoutPanelMode;
  /** true = Session wechselt mit Seitenkontext; false = „eingefroren“ (Pin) */
  followPageContext: boolean;
  /** Entwurf pro Kontext-Key (`type:id` oder `_scratch`) */
  draftByContext: Record<string, string>;
  position: { x: number; y: number };
  size: { w: number; h: number };
  unread: number;
  isStreaming: boolean;
  /** Setzt die Eingabe im Chat-Input (Quick-Prompt / Slash) */
  inputInjection: InputInjection | null;
  /** Legt Dateien in den Chat-Input (nicht persistiert — File-Objekte überleben kein Neuladen) */
  fileInjection: FileInjection | null;

  setPanelMode: (m: FlyoutPanelMode) => void;
  togglePanel: () => void;
  minimize: () => void;
  setFullscreen: (v: boolean) => void;
  setFollowPageContext: (v: boolean) => void;
  setDraftForContext: (key: string, text: string) => void;
  getDraftForContext: (key: string) => string;
  setPosition: (p: { x: number; y: number }) => void;
  setSize: (s: { w: number; h: number }) => void;
  incrementUnread: () => void;
  clearUnread: () => void;
  setStreaming: (v: boolean) => void;
  injectInput: (text: string) => void;
  clearInputInjection: () => void;
  injectFiles: (files: File[], text?: string) => void;
  clearFileInjection: () => void;
}

const SCRATCH_KEY = '_scratch';

export const useKianFlyoutStore = create<KianFlyoutState>()(
  persist(
    (set, get) => ({
      panelMode: 'closed',
      followPageContext: true,
      draftByContext: {},
      position: { x: 24, y: 96 },
      size: { w: 420, h: 640 },
      unread: 0,
      isStreaming: false,
      inputInjection: null,
      fileInjection: null,

      setPanelMode: (m) => set({ panelMode: m }),
      togglePanel: () => {
        const cur = get().panelMode;
        if (cur === 'closed' || cur === 'minimized') {
          set({ panelMode: 'docked' });
        } else {
          set({ panelMode: 'closed' });
        }
      },
      minimize: () => set({ panelMode: 'closed' }),
      setFullscreen: (v) => set({ panelMode: v ? 'fullscreen' : 'floating' }),
      setFollowPageContext: (v) => set({ followPageContext: v }),
      setDraftForContext: (key, text) =>
        set((st) => ({
          draftByContext: { ...st.draftByContext, [key]: text },
        })),
      getDraftForContext: (key) => get().draftByContext[key] ?? '',
      setPosition: (p) => set({ position: p }),
      setSize: (si) => set({ size: si }),
      incrementUnread: () => set((s) => ({ unread: s.unread + 1 })),
      clearUnread: () => set({ unread: 0 }),
      setStreaming: (v) => set({ isStreaming: v }),
      injectInput: (text) =>
        set({ inputInjection: { id: Date.now(), text } }),
      clearInputInjection: () => set({ inputInjection: null }),
      injectFiles: (files, text) => set({ fileInjection: { id: Date.now(), files, text } }),
      clearFileInjection: () => set({ fileInjection: null }),
    }),
    {
      name: 'kian.v2.flyout',
      partialize: (s) => ({
        panelMode: s.panelMode,
        followPageContext: s.followPageContext,
        draftByContext: s.draftByContext,
        position: s.position,
        size: s.size,
      }),
    }
  )
);

export { SCRATCH_KEY };
